import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Copy, Share2, CheckCircle2 } from "lucide-react";

export const SaveProjectDialog = ({ open, onOpenChange, project, link }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="rounded-none sm:max-w-lg" data-testid="save-project-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 font-display uppercase tracking-tight">
          <CheckCircle2 className="h-4 w-4 text-[#4E7C59]" /> Progetto salvato
        </DialogTitle>
      </DialogHeader>
      <p className="text-sm text-muted-foreground">
        Conserva questo link per riprendere il progetto in qualsiasi momento. Nessuna registrazione
        richiesta.
      </p>
      <div className="border border-border bg-[#F3F6F1] p-3">
        <div className="label-xs text-[#4E7C59]">Codice progetto</div>
        <div data-testid="saved-project-code" className="font-mono-t text-lg font-bold">
          {project?.code}
        </div>
      </div>
      <Input
        data-testid="saved-project-link"
        readOnly
        value={link || ""}
        onFocus={(e) => e.target.select()}
        className="rounded-none font-mono-t text-xs"
      />
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="copy-project-link"
          onClick={() => {
            navigator.clipboard?.writeText(link || "");
            toast.success("Link copiato");
          }}
          className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
        >
          <Copy className="mr-2 h-4 w-4" /> Copia link
        </Button>
        {navigator.share && (
          <Button
            data-testid="share-project-link"
            variant="outline"
            onClick={() =>
              navigator
                .share({ title: project?.name || "Progetto BioEdil", url: link })
                .catch(() => {})
            }
            className="rounded-none"
          >
            <Share2 className="mr-2 h-4 w-4" /> Condividi
          </Button>
        )}
        <Button
          data-testid="continue-configuring"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          className="rounded-none"
        >
          Continua a configurare
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const ResumeProjectDialog = ({ open, onOpenChange, onResume }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const raw = value.trim();
    if (!raw) return toast.error("Incolla il link del progetto");
    let code = raw;
    let token = "";
    try {
      const url = new URL(raw);
      token = url.searchParams.get("t") || "";
      code = decodeURIComponent(url.pathname.split("/").filter(Boolean).pop() || "");
    } catch {
      const parts = raw.split(/[?&]t=/);
      code = parts[0].split("/").filter(Boolean).pop() || raw;
      token = parts[1] || "";
    }
    if (!token) return toast.error("Il link non contiene il codice di accesso privato");
    setBusy(true);
    try {
      await onResume({ code: code.toUpperCase(), token });
      onOpenChange(false);
      setValue("");
    } catch {
      toast.error("Progetto non trovato o link non valido");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-lg" data-testid="resume-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            Riprendi progetto
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Incolla il link personale che hai ricevuto al salvataggio del progetto.
        </p>
        <Input
          data-testid="resume-project-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="https://…/progetto/BIO-8K4X2?t=…"
          className="rounded-none font-mono-t text-xs"
        />
        <Button
          data-testid="resume-project-submit"
          onClick={submit}
          disabled={busy}
          className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
        >
          Riprendi progetto
        </Button>
      </DialogContent>
    </Dialog>
  );
};
